import React from 'react';

const TermsPage: React.FC = () => {
  return (
    <div className="bg-slate-50 py-16 dark:bg-[#050916]">
      <div className="mx-auto max-w-5xl px-6 lg:px-8">
        <header className="border-b border-slate-200 pb-10 dark:border-slate-800/80">
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-600 dark:text-indigo-300">Legal</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
            Prompt Detective Terms of Service
          </h1>
          <p className="mt-3 max-w-3xl text-base text-slate-600 dark:text-white/80">
            These Terms of Service (the "Terms") form a binding agreement between you and Reverse AI Labs
            Limited ("Prompt Detective", "we", "our", or "us"). By accessing or using our products, applications,
            and services (collectively, the "Services"), you agree to be bound by these Terms.
          </p>
          <p className="mt-3 text-xs text-slate-400 dark:text-white/60">Effective date: 16 September 2024. Last updated: 16 September 2024.</p>
        </header>

        <div className="prose prose-slate mt-12 max-w-none dark:prose-invert dark:prose-headings:text-white dark:prose-p:text-white/80 dark:prose-li:text-white/80 dark:prose-strong:text-white">
          <section>
            <h2>1. Eligibility</h2>
            <p>
              You must be at least 18 years old and have the authority to enter into these Terms on behalf of yourself or
              your organization. By using the Services, you represent and warrant that you meet these requirements and that
              you will comply with all applicable laws.
            </p>
          </section>

          <section>
            <h2>2. Account Registration</h2>
            <p>
              To access certain features, you may need to create an account. You agree to provide accurate, current, and
              complete information and keep it updated. You are responsible for safeguarding your credentials and for all
              activities that occur under your account. Prompt Detective is not liable for any loss or damage arising from
              unauthorized use of your account.
            </p>
          </section>

          <section>
            <h2>3. Permitted Use</h2>
            <ul>
              <li>Use the Services solely for lawful business or personal purposes in accordance with these Terms.</li>
              <li>Comply with usage limits, fair use policies, and technical documentation.</li>
              <li>
                Ensure that the input content you submit ("Customer Data") is owned by you or that you have the
                necessary rights to provide it to us for processing.
              </li>
            </ul>
          </section>

          <section>
            <h2>4. Prohibited Activities</h2>
            <p>You agree that you will not:</p>
            <ul>
              <li>Reverse engineer, decompile, or attempt to derive the source code of the Services.</li>
              <li>Use the Services for any activities that infringe intellectual property rights or violate laws.</li>
              <li>Upload malicious content, including viruses, bots, or scripts that compromise security.</li>
              <li>Misrepresent your relationship with Prompt Detective or impersonate another person or entity.</li>
              <li>Resell, lease, or provide the Services to third parties without our written consent.</li>
            </ul>
          </section>

          <section>
            <h2>5. Service Availability</h2>
            <p>
              We strive to maintain high availability. However, the Services may be temporarily unavailable due to
              scheduled maintenance, upgrades, or unforeseen outages. We will use commercially reasonable efforts to notify
              you of planned downtime and restore services promptly.
            </p>
          </section>

          <section>
            <h2>6. Intellectual Property</h2>
            <p>
              Prompt Detective retains all rights, title, and interest in and to the Services, including documentation,
              software, and related intellectual property. No rights are granted to you other than those expressly stated
              in these Terms. Feedback you provide may be used to improve the Services without obligation or compensation.
            </p>
          </section>

          <section>
            <h2>7. Customer Data</h2>
            <p>
              You retain ownership of the Customer Data you submit. By submitting Customer Data, you grant us a
              non-exclusive, worldwide, royalty-free license to host, store, process, and transmit such data for the
              purpose of providing and improving the Services. We implement administrative, technical, and physical
              safeguards to protect Customer Data as described in our Privacy Policy.
            </p>
          </section>

          <section>
            <h2>8. Third-Party Services</h2>
            <p>
              The Services may integrate with third-party applications or APIs. We do not control and are not responsible
              for third-party services. Your use of third-party services is subject to the terms and policies of those
              providers.
            </p>
          </section>

          <section>
            <h2>9. Subscription Plans & Billing</h2>
            <p>
              Fees for paid plans are described on our pricing page and subscription agreements. You authorize us to charge
              the payment method you provide. Except as required by law, payments are non-refundable. If payment cannot be
              processed, we may suspend or terminate access to the Services.
            </p>
          </section>

          <section>
            <h2>10. Confidentiality</h2>
            <p>
              Each party agrees to keep confidential any non-public information disclosed by the other that is marked or
              reasonably understood to be confidential. Confidential information may be used solely to fulfill obligations
              under these Terms and must be protected with at least the same care used to protect one's own confidential
              information.
            </p>
          </section>

          <section>
            <h2>11. Security Disclosure Policy</h2>
            <p>
              If you discover a vulnerability, promptly notify us at security@promptdetective.ai. Do not publicly disclose
              details until we confirm remediation. We will acknowledge valid reports within 72 hours and coordinate a
              fix.
            </p>
          </section>

          <section>
            <h2>12. Disclaimers</h2>
            <p>
              THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES
              OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
              SERVICES WILL BE ERROR-FREE OR UNINTERRUPTED.
            </p>
          </section>

          <section>
            <h2>13. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, PROMPT DETECTIVE SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL,
              SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, OR FOR LOSS OF PROFITS, REVENUE, DATA, OR GOODWILL. OUR TOTAL
              LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICES SHALL NOT EXCEED THE AMOUNTS PAID BY YOU TO PROMPT
              DETECTIVE IN THE 12 MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY.
            </p>
          </section>

          <section>
            <h2>14. Term and Termination</h2>
            <p>
              These Terms remain in effect until terminated by either party. You may terminate by discontinuing use of the
              Services. We may terminate or suspend access at any time if we believe you have violated these Terms.
              Sections that by their nature should survive termination will remain in effect.
            </p>
          </section>

          <section>
            <h2>15. Governing Law & Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of India without regard to conflict of law principles. Any dispute shall
              be resolved through confidential arbitration in Bengaluru, India, conducted in English, except injunctive
              relief which may be sought in any competent court.
            </p>
          </section>

          <section>
            <h2>16. Changes to the Terms</h2>
            <p>
              We may update these Terms from time to time. Material changes will be communicated via the Service or email.
              Continued use after the effective date of changes constitutes acceptance. If you do not agree, you must stop
              using the Services.
            </p>
          </section>

          <section>
            <h2>17. Contact</h2>
            <p>
              Questions about these Terms can be sent to legal@promptdetective.ai or to our registered office in
              Bengaluru, India.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
